import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  Input,
  OnDestroy
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import {
  PrescriberCEAssociationModalClose,
  PrescriberCEAssociationModalSave
} from '@module-claims/store/prescriber.actions';
import { validatorCompareEndDate } from '@validators-global/validator-compare-date';
import { validatorRequiredFields } from '@validators-global/validator-required-fields';
import { PrescriberDetailsSimple } from '@module-claims/models/prescriber.interface';
import { IAsyncData } from '@module-claims/store/claims.state';
import { ClaimsService } from '@module-claims/services/claims.service';
import { getValidationMessage } from '@utils-global/validation-messages';
import { getISODateString } from '@utils-global/date-time';
import { ARRAY_CONFIDENCE_340B_OPTIONS } from '@module-claims/enums/confidence-340b.enum';

@Component({
  selector: 'app-ce-association-modal',
  templateUrl: './ce-association-modal.component.html',
  styleUrls: ['./ce-association-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class CeAssociationModalComponent implements OnInit, OnDestroy {
  @Input() npi!: string;

  form!: UntypedFormGroup;
  confidenceOptions = ARRAY_CONFIDENCE_340B_OPTIONS;

  prescriberDetails$!: Observable<IAsyncData<PrescriberDetailsSimple>>;

  constructor(
    private _fb: UntypedFormBuilder,
    private _store: Store,
    private _claimsService: ClaimsService
  ) {
    this.prescriberDetails$ = this._claimsService.prescriberDetails$;
  }

  ngOnInit(): void {
    this.initForm();
  }

  ngOnDestroy(): void {
    this.close();
  }

  close(): void {
    this._store.dispatch(new PrescriberCEAssociationModalClose());
  }

  save(): void {
    if (this.form.pristine || this.form.invalid) {
      for (const key of Object.keys(this.form.controls)) {
        this.form.get(key)?.markAsDirty();
      }
      return;
    }

    this._store.dispatch(
      new PrescriberCEAssociationModalSave({
        action: 'add',
        npi: this.npi,
        ceId:
          this.ceId.value && (this.ceId.value as string).trim().toUpperCase(),
        parentCEId:
          this.parentCEId.value &&
          (this.parentCEId.value as string).trim().toUpperCase(),
        confidence: this.confidence.value.toString(),
        startDate:
          this.startDate.value && getISODateString(this.startDate.value),
        endDate: this.endDate.value && getISODateString(this.endDate.value)
      })
    );
  }

  private initForm(): void {
    this.form = this._fb.group(
      {
        ceId: [null, Validators.required],
        parentCEId: [null, Validators.required],
        confidence: [null, Validators.required],
        startDate: [null, Validators.required],
        endDate: [null]
      },
      {
        validators: [
          validatorCompareEndDate('startDate', 'endDate'),
          validatorRequiredFields('startDate')
        ]
      }
    );
  }

  getControlErrorMsg(control: UntypedFormControl): string {
    const msg = getValidationMessage(control);
    return msg.includes('date') ? '' : msg;
  }

  get ceId(): UntypedFormControl {
    return this.form.get('ceId') as UntypedFormControl;
  }
  get parentCEId(): UntypedFormControl {
    return this.form.get('parentCEId') as UntypedFormControl;
  }
  get confidence(): UntypedFormControl {
    return this.form.get('confidence') as UntypedFormControl;
  }
  get startDate(): UntypedFormControl {
    return this.form.get('startDate') as UntypedFormControl;
  }
  get endDate(): UntypedFormControl {
    return this.form.get('endDate') as UntypedFormControl;
  }
}
